import React, {useCallback, useEffect, useRef, useState} from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useOrganizationApi } from "../Services/OrganizationApiProvider";
import Breadcrumbs from "../Components/Breadcrumbs";
import { useChatAnalyticsApi } from "../Services/ChatAnalyticsApiProvider";
import Table from "../Components/Tables";
import AnalyticsExpandableWrapper from "../Components/AnalyticsExpandableRow";
import "../Components/TotalAnalytics.css";
import "../Components/OrganizationDetail.css";
import { useAuth } from "../Contexts/AuthContext";
import { AlertProvider, useAlert } from "../Components/AlertContext";
import DateRangeSelector, {getDateRangeByPeriod} from "../Components/DateRangeSelector";


function OrganizationContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useAlert();
  const { status } = useAuth();
  const { organizationId } = useParams();
  const { getOrganizationById } = useOrganizationApi();
  const { getTotalChatAnalytics, getDetailedChatAnalytics, getTokenAnalytics } = useChatAnalyticsApi();

  const [organization, setOrganization] = useState();
  const [analytics, setAnalytics] = useState([]);
  const [detailedAnalytics, setDetailedAnalytics] = useState([]);
  const [tokenAnalytics, setTokenAnalytics] = useState([]);
  const hasOrganizationsPermission = status?.permissions?.includes("all:organizations");

  const alertShownRef = useRef(false);

  const [totalStartDate, setTotalStartDate] = useState();
  const [totalEndDate, setTotalEndDate] = useState();

  const [detailedStartDate, setDetailedStartDate] = useState();
  const [detailedEndDate, setDetailedEndDate] = useState();

  const [tokenStartDate, setTokenStartDate] = useState();
  const [tokenEndDate, setTokenEndDate] = useState();

  const [totalChannels, setTotalChannels] = useState(0);
  const [channelsPerPage, setChannelsPerPage] = useState(10);

  const columns = [
    { name: "ID", selector: row => row.channel_id },
    { name: "Channel Name", selector: row => row.channel_name },
    { name: "Total Chats", selector: row => row.total_chats, sortable: true },
    { name: "Active Chats", selector: row => row.active_chats, sortable: true },
    { name: "Inactive Chats", selector: row => row.inactive_chats, sortable: true }
  ];

  useEffect(() => {
    if (location.state?.successMessage && !alertShownRef.current) {
      showAlert("success", location.state.successMessage);
      alertShownRef.current = true;
      window.history.replaceState({}, document.title);
    }
  }, [location, showAlert]);

  useEffect(() => {
    const fetchOrganization = async () => {
      try {
        const shouldSkipCache = !!location.state?.successMessage;
        const org = await getOrganizationById(organizationId, shouldSkipCache);
        setOrganization(org);
      } catch (error) {
        console.error("Error fetching organization:", error);
        navigate("/404");
      }
    };
    fetchOrganization();
  }, [organizationId, getOrganizationById, navigate]);

  useEffect(() => {
    const fetchChatAnalytics = async () => {
      let start = totalStartDate;
      let end = totalEndDate;

      if (!start || !end) {
        const range = getDateRangeByPeriod("day");
        start = range.start_date;
        end = range.end_date;
      }
      try {
        const analyticsData = await getTotalChatAnalytics(start, end);
        setAnalytics(analyticsData);
      } catch (error) {
        console.error("Error fetching total analytics:", error);
      }
    };

    fetchChatAnalytics();
  }, [totalStartDate, totalEndDate, getTotalChatAnalytics]);

  const fetchDetailedAnalytics = async (skip = 0, limit = channelsPerPage) => {
    if (limit !== channelsPerPage) {
        setChannelsPerPage(limit);
    }
    let start = detailedStartDate;
    let end = detailedEndDate;

    if (!start || !end) {
      const range = getDateRangeByPeriod("day");
      start = range.start_date;
      end = range.end_date;
    }
    try {
      const response = await getDetailedChatAnalytics(start, end, skip, limit);
      setDetailedAnalytics(response.analyticsData);
      setTotalChannels(response.items);

      } catch (error) {
        console.error("Error fetching detailed analytics:", error);
      }
    };

  useEffect(() => {
    fetchDetailedAnalytics(0, channelsPerPage);
  }, [detailedStartDate, detailedEndDate, getDetailedChatAnalytics]);

  const fetchTokenAnalytics = async () => {
    let start = tokenStartDate;
    let end = tokenEndDate;

    if (!start || !end) {
      const range = getDateRangeByPeriod("day");
      start = range.start_date;
      end = range.end_date;
    }
    try {
      const analyticsData = await getTokenAnalytics(start, end);
      setTokenAnalytics(analyticsData);
    } catch (error) {
      console.error("Error fetching token analytics:", error);
    }
  };

  useEffect(() => {
    if (hasOrganizationsPermission) {
      fetchTokenAnalytics();
    }
  }, [tokenStartDate, tokenEndDate, getTokenAnalytics, status?.permissions]);

  const handlePeriodChange = useCallback((period) => {
    setTotalStartDate(period?.start_date);
    setTotalEndDate(period?.end_date);
  }, []);

  const handleDetailedPeriodChange = useCallback((period) => {
    setDetailedStartDate(period?.start_date);
    setDetailedEndDate(period?.end_date);
  }, []);

  const handleTokenPeriodChange = useCallback((period) => {
    setTokenStartDate(period?.start_date);
    setTokenEndDate(period?.end_date);
  }, []);

  if (!organization) return <div>Organization not found.</div>;
  if (!analytics) return <div>Loading analytics...</div>;

  return (
      <div className="container">
        <Breadcrumbs />
        <div className="row d-flex gap">
          <div className="radius-yes p-2 bg-yes col-12">
            <div className="org-details-header">
              <h2>Organization Details</h2>
                <button
                  className="edit-btn"
                  onClick={() => {
                    navigate(`/organizations/${organizationId}/detail`);
                  }}
                  title="Edit parameter"
                >
                  <i className="fa-solid fa-pencil"></i>
                </button>
          </div>

            <div className="org-header">
              <h2>{organization.title}</h2>
              <span className={`status-badge ${organization.is_active ? 'active' : 'inactive'}`}>
                {organization.is_active ? "Active" : "Inactive"}
              </span>
            </div>
            <div className="detail-item">
              <label>ID:</label>
              <span>{organization.id}</span>
            </div>
            <div className="detail-item">
              <label>Description:</label>
              <p>{organization.description || "No description available."}</p>
            </div>
            <div className="detail-item">
              <label>Active Communications:</label>
              <p>{organization?.active_communications}</p>
            </div>
            <div className="detail-item">
              <label>Communications Per Day:</label>
              <p>{organization?.communications_per_day}</p>
            </div>
            <div className="detail-item">
              <label>Communications Per Month:</label>
              <p>{organization?.communications_per_month}</p>
            </div>
          </div>

          <div className="radius-yes p-2 bg-yes col-12">
            <h2 className="mb-4">Total Analytics</h2>
            <div className="analytics-grid">

              <div className="analytics-card">
                <div className="analytics-header">
                  <h3>Total Chats</h3>
                </div>
                <div className="analytics-value">{analytics.total_chats?.toLocaleString() || 0}</div>
                <div className="analytics-details">
                  <div className="detail-item">
                    <span className="label">Open chats</span>
                    <span className="value">{analytics.active_chats?.toLocaleString() || 0}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Closed chats</span>
                    <span className="value">{analytics.inactive_chats?.toLocaleString() || 0}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Used chats</span>
                    <span className="value">{analytics.used_chats?.toLocaleString() || 0}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Average total message per chat</span>
                    <span className="value">{analytics.total_message_per_thread?.toLocaleString() || 0}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Average AI message per chat</span>
                    <span className="value">{analytics.ai_message_per_thread?.toLocaleString() || 0}</span>
                  </div>
                </div>
              </div>

              <div className="analytics-card">
                <div className="analytics-header">
                  <h3>Total Messages</h3>
                </div>
                <div className="analytics-value">{analytics.total_messages?.toLocaleString() || 0}</div>
                <div className="analytics-details">
                  <div className="detail-item">
                    <span className="label">Incoming</span>
                    <span className="value">{analytics.input_messages?.toLocaleString() || 0}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Outgoing</span>
                    <span className="value">{analytics.output_messages?.toLocaleString() || 0}</span>
                  </div>
                </div>
              </div>
              <div className="analytics-card">
                <div className="analytics-header">
                  <h3>AI Statistics</h3>
                </div>
                <div className="analytics-details">
                  <div className="detail-item">
                    <span className="label">Generated messages</span>
                    <span className="value">{analytics.generated_messages?.toLocaleString() || 0}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Sent messages</span>
                    <span className="value">{analytics.is_sent?.toLocaleString() || 0}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Redacted messages</span>
                    <span className="value">{analytics.is_redacted?.toLocaleString() || 0}</span>
                  </div>
                </div>
              </div>

              {analytics.total_tokens != null && (
                  <div className="analytics-card">
                    <div className="analytics-header">
                      <h3>Total Tokens</h3>
                    </div>
                    <div className="analytics-value">{analytics.total_tokens?.toLocaleString() || 0}</div>
                    <div className="analytics-details">
                      <div className="detail-item">
                        <span className="label">Input tokens</span>
                        <span className="value">{analytics.input_tokens?.toLocaleString() || 0}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Output tokens</span>
                        <span className="value">{analytics.output_tokens?.toLocaleString() || 0}</span>
                      </div>
                    </div>
                  </div>
              )}
            </div>

            <div style={{marginTop: "6vh"}}>
              <DateRangeSelector onChange={handlePeriodChange}/>
            </div>
          </div>

          <div className="radius-yes p-2 bg-yes col-12">
            <h2>Channels</h2>
            {totalChannels === 0 ? (
                <p>No detailed data available.</p>
            ) : (
                <Table
                    key={`${tokenStartDate}-${tokenEndDate}`}
                    columns={columns}
                    data={detailedAnalytics}
                    selectableRowsHighlight
                    expandableRows
                    expandableRowsComponent={AnalyticsExpandableWrapper}
                    paginationPerPage={channelsPerPage}
                    paginationTotalRows={totalChannels}
                    sortIcon={<i className="fa-solid fa-chevron-down"/>}
                    onChangePage={fetchDetailedAnalytics}
                />
            )}
            <DateRangeSelector onChange={handleDetailedPeriodChange}/>
          </div>
          {hasOrganizationsPermission && (
            <div className="radius-yes p-2 bg-yes col-12">
              <h2>LLM Analytics</h2>
              {!tokenAnalytics || tokenAnalytics.length === 0 ? (
                  <p>No LLM analytics data available.</p>
              ) : (
                  <Table
                      key={`${tokenStartDate}-${tokenEndDate}`}
                      columns={[
                        {name: "Model", selector: row => row.model_name, sortable: true},
                        {name: "Total Tokens", selector: row => row.total_tokens, sortable: true},
                        {name: "Input Tokens", selector: row => row.input_tokens, sortable: true},
                        {name: "Output Tokens", selector: row => row.output_tokens, sortable: true}
                      ]}
                      data={tokenAnalytics || []}
                      selectableRowsHighlight
                      paginationPerPage={channelsPerPage}
                      paginationTotalRows={tokenAnalytics ? tokenAnalytics.length : 0}
                      sortIcon={<i className="fa-solid fa-chevron-down"/>}
                      onChangePage={fetchTokenAnalytics}
                  />
              )}
               <DateRangeSelector onChange={handleTokenPeriodChange}/>
            </div>
          )}
        </div>

      </div>
  );
}

export default function Organization() {
  return (
    <AlertProvider>
      <OrganizationContent />
    </AlertProvider>
  );
}
